import React from "react";
import { motion } from "framer-motion";
import {
  Sparkles,
  Wrench,
  Droplets,
  ShieldCheck,
  KeyRound,
  CheckCircle2,
  Hammer,
  Zap,
} from "lucide-react";

const CHIPS = [
  { icon: Wrench,      label: "Lackkratzer entfernt" },
  { icon: Hammer,      label: "Dellen ausgebeult" },
  { icon: Sparkles,    label: "Innenraum aufbereitet" },
  { icon: Droplets,    label: "Politur & Versiegelung" },
  { icon: ShieldCheck, label: "Felgen aufgearbeitet" },
  { icon: KeyRound,    label: "Übergabe-bereit" },
];

// Mercedes-Benz – different from the hero BMW M5
const SHOWCASE_IMG =
  "https://images.unsplash.com/photo-1618843479313-40f8afb4b4d8?auto=format&fit=crop&w=1800&q=80";
const SHOWCASE_IMG_FALLBACK =
  "https://images.unsplash.com/photo-1606664515524-ed2f786a0bd6?auto=format&fit=crop&w=1800&q=80";

const ChipCard = ({ icon: Icon, label, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.5, delay }}
    className="flex items-center gap-3 bg-white/95 backdrop-blur rounded-2xl px-4 py-3 shadow-xl shadow-black/20 border border-white/40"
  >
    <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#1FA3B5] to-[#0F4651] flex items-center justify-center flex-shrink-0 shadow-lg shadow-[#1FA3B5]/40">
      <Icon className="w-4 h-4 text-white" />
    </span>
    <span className="text-sm font-semibold text-slate-900 leading-tight flex-1">
      {label}
    </span>
    <CheckCircle2 className="w-5 h-5 text-[#1FA3B5] flex-shrink-0" />
  </motion.div>
);

const ShowcaseStrip = () => {
  return (
    <section
      id="showcase"
      data-testid="showcase-section"
      className="relative py-20 sm:py-24 lg:py-32 bg-gradient-to-b from-slate-900 via-[#0F2A33] to-slate-900 overflow-hidden text-white"
    >
      {/* Grid overlay */}
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(31,163,181,0.22)_0%,_transparent_60%)] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto"
        >
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#1FA3B5]/15 border border-[#1FA3B5]/30 backdrop-blur text-[#5DD8E5] text-xs font-semibold mb-5">
            <Zap className="w-3.5 h-3.5" /> Was wir am Rückläufer machen
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
            Wir reparieren alles,<br />
            <span className="bg-gradient-to-r from-[#5DD8E5] to-[#1FA3B5] bg-clip-text text-transparent">
              was Sie sonst extra zahlen.
            </span>
          </h2>
          <p className="mt-4 sm:mt-5 text-sm sm:text-base lg:text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed">
            Jeder Kratzer, jede Delle, jeder Innenraum-Fleck – wir machen Ihr Fahrzeug
            so rückgabe-fertig, dass der Leasinggeber keinen Grund findet, Sie zur Kasse zu bitten.
          </p>
        </motion.div>

        {/* ===== Layout: Mobile (stacked) vs Desktop (3-col with chips around image) ===== */}
        <div className="mt-10 sm:mt-14 lg:mt-16 grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6 items-center">
          {/* Left chips column — desktop only */}
          <div className="hidden lg:flex flex-col gap-4 lg:col-span-3">
            {CHIPS.slice(0, 3).map((c, i) => (
              <ChipCard key={i} icon={c.icon} label={c.label} delay={0.1 + i * 0.08} />
            ))}
          </div>

          {/* Main image (stretched, wider) */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
            className="relative lg:col-span-6 w-full aspect-[16/10] sm:aspect-[16/9] rounded-2xl sm:rounded-[2rem] overflow-hidden shadow-2xl shadow-[#1FA3B5]/30 border border-white/10"
          >
            {/* Pulsing ring behind */}
            <div className="absolute -inset-2 sm:-inset-3 rounded-[2rem] sm:rounded-[2.5rem] bg-[#1FA3B5]/20 blur-xl -z-10" />

            <motion.img
              src={SHOWCASE_IMG}
              onError={(e) => { e.currentTarget.src = SHOWCASE_IMG_FALLBACK; }}
              alt="Premium Aufbereitung – Rückläufer-Service"
              className="w-full h-full object-cover"
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
            />

            {/* Scan-line FX */}
            <motion.div
              initial={{ y: "-100%" }}
              animate={{ y: "100%" }}
              transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
              className="absolute inset-x-0 h-[2px] bg-gradient-to-r from-transparent via-[#5DD8E5] to-transparent shadow-[0_0_20px_2px_rgba(93,216,229,0.7)] pointer-events-none"
            />

            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/75 via-slate-900/10 to-slate-900/30" />

            {/* Top-left badge */}
            <div className="absolute top-3 sm:top-5 left-3 sm:left-5">
              <div className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full bg-white/95 backdrop-blur text-[10px] sm:text-xs font-bold text-slate-900 uppercase tracking-wider shadow-lg">
                <ShieldCheck className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-[#1FA3B5]" />
                Rückgabe-Ready
              </div>
            </div>

            {/* Bottom row: Live + 100% */}
            <div className="absolute bottom-3 sm:bottom-5 left-3 sm:left-5 right-3 sm:right-5 flex items-center justify-between gap-2">
              <div className="inline-flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full bg-white/15 backdrop-blur border border-white/20 text-[10px] sm:text-xs font-semibold">
                <span className="relative flex h-1.5 w-1.5 sm:h-2 sm:w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#5DD8E5] opacity-75" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 sm:h-2 sm:w-2 bg-[#5DD8E5]" />
                </span>
                Live-Analyse
              </div>
              <div className="inline-flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full bg-[#1FA3B5] text-[10px] sm:text-xs font-bold uppercase tracking-wider shadow-lg shadow-[#1FA3B5]/40">
                <CheckCircle2 className="w-3 h-3 sm:w-3.5 sm:h-3.5" /> 100% Rückgabe-Ready
              </div>
            </div>
          </motion.div>

          {/* Right chips column — desktop only */}
          <div className="hidden lg:flex flex-col gap-4 lg:col-span-3">
            {CHIPS.slice(3, 6).map((c, i) => (
              <ChipCard key={i} icon={c.icon} label={c.label} delay={0.1 + i * 0.08} />
            ))}
          </div>

          {/* Mobile + Tablet chips grid — below image */}
          <div className="lg:hidden grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3 mt-2">
            {CHIPS.map((c, i) => (
              <ChipCard key={i} icon={c.icon} label={c.label} delay={0.1 + i * 0.06} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ShowcaseStrip;
